import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Chip,
  Skeleton,
  CircularProgress,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Avatar,
  IconButton,
  useMediaQuery,
  useTheme,
  Divider,
  Button,
  Stack,
} from "@mui/material";
import {
  ChevronLeft,
  Close,
  Category as CategoryIcon,
  Storefront,
} from "@mui/icons-material";
import { getVendorCategories, getVendorProducts } from "../../../services/api/vendor";
import Footer from "../../common/Footer/Footer";
import { useSelector } from "react-redux";

const PAGE_SIZE = 20;

// ─── Skeleton Components ───────────────────────────────────────────────────

const CategorySkeleton = () => (
  <Box sx={{ mb: 1 }}>
    <Skeleton variant="circular" width={40} height={40} sx={{ mx: "auto" }} />
    <Skeleton width="80%" height={12} sx={{ mx: "auto", mt: 1 }} />
  </Box>
);

const ProductSkeleton = () => (
  <Box sx={{ borderRadius: "14px", overflow: "hidden", border: "1.5px solid #eaeef5" }}>
    <Skeleton variant="rectangular" height={160} sx={{ bgcolor: "#f0f4f8" }} />
    <Box sx={{ p: 1.2 }}>
      <Skeleton width="90%" height={14} sx={{ mb: 0.5, bgcolor: "#f0f4f8" }} />
      <Skeleton width="60%" height={12} sx={{ mb: 0.5, bgcolor: "#f0f4f8" }} />
    </Box>
  </Box>
);

// ─── Category Item Component ────────────────────────────────────────────────

const CategoryItem = ({ category, isSelected, onClick }) => (
  <ListItemButton
    selected={isSelected}
    onClick={() => onClick(category)}
    sx={{
      borderRadius: 2,
      mb: 0.5,
      flexDirection: "column",
      textAlign: "center",
    //   py: 1.5,
      "&.Mui-selected": {
        backgroundColor: "#fff3e0",
        "&:hover": {
          backgroundColor: "#fff3e0",
        },
      },
      "&:hover": {
        backgroundColor: "#faf5ff",
      },
    }}
  >
    <Avatar
      sx={{
        width: 48,
        height: 48,
        bgcolor: isSelected ? "#f5af19" : "#f0f0f0",
        color: isSelected ? "white" : "#666",
        mb: 1,
      }}
    >
      {category.imageUrl ? (
        <img
          src={category.imageUrl}
          alt={category.name}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <CategoryIcon />
      )}
    </Avatar>
    <Typography
      variant="caption"
      sx={{
        fontWeight: isSelected ? 700 : 500,
        color: isSelected ? "#f5af19" : "#666",
        fontSize: "11px",
        wordBreak: "break-word",
      }}
    >
      {category.name}
    </Typography>
  </ListItemButton>
);

// ─── Product Card Component (matching ProductsGrid style, no price) ─────────

const VendorProductCard = React.memo(({ product, onClick }) => {
  const discount = product.discount || 
    (product.mrp && product.salePrice && product.mrp > product.salePrice
      ? Math.round(((product.mrp - product.salePrice) / product.mrp) * 100)
      : null);

  const handleClick = () => {
    if (product.productUuid && onClick) onClick(product.productUuid);
  };

  return (
    <Card
      onClick={handleClick}
      sx={{
        border: "1.5px solid #eaeef5",
        borderRadius: "14px",
        boxShadow: "none",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        transition: "all 0.22s ease",
        cursor: "pointer",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 32px rgba(15,76,129,0.12)",
          borderColor: "#c5d4ee",
        },
      }}
    >
      {discount && discount > 0 && (
        <Chip
          label={`${discount}% OFF`}
          size="small"
          sx={{
            position: "absolute", top: 8, left: 8, zIndex: 2,
            fontSize: 9, fontWeight: 800, height: 20,
            background: "#fff3e0", color: "#e65100",
          }}
        />
      )}

      <Box
        sx={{
          height: 160,
          background: "#FEFEFE",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {product.primaryImage?.url || product.imageUrl ? (
          <CardMedia
            component="img"
            image={product.primaryImage?.url || product.imageUrl}
            alt={product.name}
            sx={{ width: "80%", height: "80%", objectFit: "contain" }}
          />
        ) : (
          <Storefront sx={{ fontSize: 48, color: "#ccc" }} />
        )}
      </Box>

      <CardContent sx={{ p: "10px !important", display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Typography
          sx={{
            fontSize: 12.5, fontWeight: 700, color: "#1a1a2e", lineHeight: 1.35,
            display: "-webkit-box", WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical", overflow: "hidden",
          }}
        >
          {product.name}
        </Typography>
        {product.unit && (
          <Typography sx={{ fontSize: 11, color: "#999", fontWeight: 600 }}>
            {product.unit}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
});

// ─── Mobile Category Drawer ─────────────────────────────────────────────────

const MobileCategoryDrawer = ({ open, onClose, categories, selectedCategory, onCategorySelect }) => (
  <Drawer
    anchor="left"
    open={open}
    onClose={onClose}
    sx={{
      "& .MuiDrawer-paper": {
        width: 240,
        bgcolor: "#ffffff",
        borderRadius: "0 16px 16px 0",
      },
    }}
  >
    <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 16 }}>
        Categories
      </Typography>
      <IconButton onClick={onClose} size="small">
        <Close />
      </IconButton>
    </Box>
    <Divider />
    <List >
      {categories.map((category) => (
        <CategoryItem
          key={category.id}
          category={category}
          isSelected={selectedCategory?.id === category.id}
          onClick={(cat) => {
            onCategorySelect(cat);
            onClose();
          }}
        />
      ))}
    </List>
  </Drawer>
);

// ─── Main Vendor Category Page ──────────────────────────────────────────────

const VendorCategoryPage = ({ vendorId, vendorName, onBack, onProductClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const zipcode = useSelector((state) => state.orders?.postalcode);

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [error, setError] = useState(null);

  // Pagination states
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalProducts, setTotalProducts] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const isFetchingRef = useRef(false);
  const sentinelRef = useRef(null);
  const observerRef = useRef(null);

  // Fetch categories on mount
  useEffect(() => {
    if (vendorId) {
      fetchCategories();
    }
  }, [vendorId]);

  const fetchCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getVendorCategories(vendorId);
      if (response.data.success) {
        const categoryList = response.data.data || [];
        setCategories(categoryList);
        if (categoryList.length > 0) {
          setSelectedCategory(categoryList[0]);
        }
      } else {
        setError(response.data.message || "Failed to load categories");
      }
    } catch (err) {
      console.error("Error fetching categories:", err);
      setError(err.response?.data?.message || "Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch products when category changes
  const fetchProducts = useCallback(async (categoryId, pageNum = 1, isFirst = true) => {
    if (!categoryId) return;
    if (isFetchingRef.current) return;
    
    isFetchingRef.current = true;
    isFirst ? setProductsLoading(true) : setLoadingMore(true);

    try {
      const response = await getVendorProducts({
        vendorId: vendorId,
        categoryId: categoryId,
        page: pageNum,
        limit: PAGE_SIZE,
        postalCode: zipcode ? String(zipcode) : "",
        isActive: true,
      });

      const data = response?.data?.data;
      const newProducts = data?.products || [];
      const total = data?.pagination?.total || 0;

      if (isFirst) {
        setProducts(newProducts);
        setPage(pageNum + 1);
      } else {
        setProducts((prev) => [...prev, ...newProducts]);
        setPage(pageNum + 1);
      }

      setTotalProducts(total);
      const more = newProducts.length === PAGE_SIZE && (pageNum * PAGE_SIZE) < total;
      setHasMore(more);
    } catch (err) {
      console.error("Error fetching products:", err);
      setHasMore(false);
    } finally {
      isFetchingRef.current = false;
      setProductsLoading(false);
      setLoadingMore(false);
    }
  }, [vendorId, zipcode]);

  // Reset and fetch when selected category changes
  useEffect(() => {
    if (selectedCategory) {
      setProducts([]);
      setPage(1);
      setHasMore(true);
      setTotalProducts(0);
      isFetchingRef.current = false;
      fetchProducts(selectedCategory.id, 1, true);
    }
  }, [selectedCategory, fetchProducts]);

  // Load more products
  const loadMore = useCallback(() => {
    if (!hasMore || isFetchingRef.current || !selectedCategory) return;
    fetchProducts(selectedCategory.id, page, false);
  }, [hasMore, page, fetchProducts, selectedCategory]);

  // Setup intersection observer for infinite scroll
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();
    if (!hasMore || productsLoading) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "0px 0px 200px 0px", threshold: 0 }
    );

    if (sentinelRef.current) observerRef.current.observe(sentinelRef.current);

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [loadMore, hasMore, productsLoading]);

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
  };

  const handleProductClickLocal = (productUuid) => {
    if (onProductClick) {
      onProductClick(productUuid);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, bgcolor: "#fafafa", minHeight: "100vh" }}>
        <Box sx={{ maxWidth: 1400, mx: "auto" }}>
          <Box sx={{ display: "flex", gap: 3 }}>
            <Box sx={{ width: 240, display: { xs: "none", md: "block" } }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <CategorySkeleton key={i} />
              ))}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                  gap: 1.8,
                }}
              >
                {Array(12).fill(0).map((_, i) => (
                  <ProductSkeleton key={i} />
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  if (error && categories.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: "center", bgcolor: "#fafafa", minHeight: "100vh" }}>
        <Typography variant="h6" color="error" gutterBottom>
          {error}
        </Typography>
        <Button variant="contained" onClick={fetchCategories} sx={{ mt: 2 }}>
          Retry
        </Button>
      </Box>
    );
  }

  const sidebarWidth = isTablet ? 200 : 240;

  return (
    <Box sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
      {/* Header with back button */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          bgcolor: "#ffffff",
          borderBottom: "1px solid #eaeef5",
          px: { xs: 2, md: 3 },
          py: 1.5,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <IconButton onClick={onBack} sx={{ bgcolor: "#f5f5f5" }}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 800, color: "#1a1a1a" }}>
            {vendorName || "Vendor Store"}
          </Typography>
          {isMobile && categories.length > 0 && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setMobileDrawerOpen(true)}
              sx={{ ml: "auto", borderColor: "#f5af19", color: "#f5af19" }}
            >
              Categories ({categories.length})
            </Button>
          )}
        </Stack>
      </Box>

      {/* Mobile Category Drawer */}
      <MobileCategoryDrawer
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        categories={categories}
        selectedCategory={selectedCategory}
        onCategorySelect={handleCategorySelect}
      />

      {/* Main Content */}
      <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 2, md: 3 }, py: 3 }}>
        <Box sx={{ display: "flex", gap: 3 }}>
          {/* Desktop Sidebar - Categories */}
          <Box
            sx={{
              width: sidebarWidth,
              flexShrink: 0,
              display: { xs: "none", md: "block" },
              position: "sticky",
              top: 80,
              alignSelf: "flex-start",
              maxHeight: "calc(100vh - 100px)",
              overflowY: "auto",
              bgcolor: "#ffffff",
              borderRadius: 2,
              p: 1,
              border: "1px solid #eaeef5",
            }}
          >
            {/* <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, p: 1, color: "#f5af19", fontSize: 12 }}
            >
              ALL CATEGORIES
            </Typography> */}
            <Divider sx={{ mb: 1 }} />
            <List sx={{ p: 0 }}>
              {categories.map((category) => (
                <CategoryItem
                  key={category.id}
                  category={category}
                  isSelected={selectedCategory?.id === category.id}
                  onClick={handleCategorySelect}
                />
              ))}
            </List>
          </Box>

          {/* Products Area */}
          <Box sx={{ flex: 1 }}>
            {/* Selected Category Header */}
            {selectedCategory && (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#1a1a2e" }}>
                  {selectedCategory.name}
                </Typography>
                {!productsLoading && products.length > 0 && (
                  <Chip
                    label={totalProducts > 0 ? `${totalProducts} items` : `${products.length} loaded`}
                    size="small"
                    sx={{ fontSize: 11, fontWeight: 700, background: "#f0f4ff", color: "#0f4c81" }}
                  />
                )}
              </Box>
            )}

            {/* Products Grid - 6 cards per row */}
            {productsLoading && products.length === 0 ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                  gap: 1.8,
                }}
              >
                {Array(12).fill(0).map((_, i) => (
                  <ProductSkeleton key={i} />
                ))}
              </Box>
            ) : products.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Storefront sx={{ fontSize: 64, color: "#ccc", mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: "#666" }}>
                  No products in this category
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please check back later for new items
                </Typography>
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                    gap: 1.8,
                  }}
                >
                  {products.map((product, idx) => (
                    <VendorProductCard
                      key={product.vendorProductId || idx}
                      product={product}
                      onClick={handleProductClickLocal}
                    />
                  ))}
                </Box>

                {/* Loading More Indicator */}
                {loadingMore && (
                  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4, gap: 1.5 }}>
                    <CircularProgress size={22} sx={{ color: "#0f4c81" }} />
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0f4c81" }}>
                      Loading more products...
                    </Typography>
                  </Box>
                )}

                {/* Sentinel for infinite scroll */}
                {!productsLoading && hasMore && !loadingMore && (
                  <Box ref={sentinelRef} sx={{ height: 1 }} />
                )}

                {/* End of products message */}
                {!hasMore && products.length > 0 && (
                  <Box sx={{ textAlign: "center", py: 2, mx: 2, borderTop: "1px solid #f0f0f0" }}>
                    <Typography sx={{ fontSize: 12, color: "#bbb", fontWeight: 600 }}>
                      ✓ All {products.length} products shown
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>

      <Footer />
    </Box>
  );
};

export default VendorCategoryPage;